type CompanyApiItem = {
  data?: {
    company?: {
      key?: string;
      title?: string;
      visible?: boolean;
      cta?: Array<{
        heroSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          headline?: string;
          primaryButton?: {
            key?: string;
            label?: string;
            visible?: boolean;
          };
        };
        joinusSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          content?: Array<{
            cardSection?: {
              key?: string;
              label?: string;
              title?: string;
              visible?: boolean;
              imageSrc?: string;
              description?: string;
              list?: Array<{
                id?: number;
                step?: string;
                title?: string;
                description?: string;
                button?: {
                  title?: string;
                };
              }>;
            };
          }>;
        };
        contactSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          content?: Array<{
            cardSection?: {
              key?: string;
              label?: string;
              title?: string;
              visible?: boolean;
              imageSrc?: string;
              description?: string;
              list?: Array<{
                id?: string | number;
                title?: string;
                pathUrl?: string;
                paragraph?: string;
                buttonText?: string;
              }>;
            };
          }>;
        };
        ourValueSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          content?: Array<{
            cardSection?: {
              key?: string;
              label?: string;
              title?: string;
              visible?: boolean;
              description?: string;
              list?: Array<{
                id?: number;
                title?: string;
                iconAlt?: string;
                iconSrc?: string;
                description?: string;
              }>;
            };
          }>;
        };
        ourmissionSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          content?: Array<{
            headerSection?: {
              key?: string;
              image?: string;
              label?: string;
              title?: string;
              visible?: boolean;
              description?: string;
            };
          }>;
        };
        differentiatorSection?: {
          key?: string;
          type?: string;
          author?: string;
          status?: string;
          visible?: boolean;
          content?: Array<{
            cardSection?: {
              key?: string;
              label?: string;
              title?: string;
              visible?: boolean;
              description?: string;
              list?: Array<{
                id?: number;
                title?: string;
                iconAlt?: string;
                iconSrc?: string;
                description?: string;
              }>;
            };
          }>;
        };
      }>;
    };
  };
};

export type NormalizedCompany = {
  page: "company";
  title: string;
  visible: boolean;

  hero: {
    headline: string;
    visible: boolean;
    primaryButton: {
      label: string;
      visible: boolean;
    };
  };

  joinUs: {
    visible: boolean;
    label: string;
    title: string;
    description: string;
    imageSrc: string;
    items: Array<{
      id: number;
      step: string;
      title: string;
      description: string;
      buttonTitle: string;
    }>;
  };

  contact: {
    visible: boolean;
    label: string;
    title: string;
    description: string;
    imageSrc: string;
    items: Array<{
      id: string;
      title: string;
      pathUrl: string;
      paragraph: string;
      buttonText: string;
    }>;
  };

  ourValue: {
    visible: boolean;
    label: string;
    title: string;
    description: string;
    items: Array<{
      id: number;
      title: string;
      iconAlt: string;
      iconSrc: string;
      description: string;
    }>;
  };

  ourMission: {
    visible: boolean;
    label: string;
    title: string;
    description: string;
    image: string;
  };

  differentiator: {
    visible: boolean;
    label: string;
    title: string;
    description: string;
    items: Array<{
      id: number;
      title: string;
      iconAlt: string;
      iconSrc: string;
      description: string;
    }>;
  };

  meta: {
    key: string;
  };
};

export function normalizeCompany(item: CompanyApiItem): NormalizedCompany | null {
  const company = item?.data?.company;
  const cta = company?.cta?.[0];

  const hero = cta?.heroSection;

  const joinUsSection = cta?.joinusSection;
  const joinUsCard = joinUsSection?.content?.[0]?.cardSection;

  const contactSection = cta?.contactSection;
  const contactCard = contactSection?.content?.[0]?.cardSection;

  const ourValueSection = cta?.ourValueSection;
  const ourValueCard = ourValueSection?.content?.[0]?.cardSection;

  const ourMissionSection = cta?.ourmissionSection;
  const ourMissionHeader = ourMissionSection?.content?.[0]?.headerSection;

  const differentiatorSection = cta?.differentiatorSection;
  const differentiatorCard = differentiatorSection?.content?.[0]?.cardSection;

  if (!company || !cta) return null;

  return {
    page: "company",
    title: company.title ?? "",
    visible: Boolean(company.visible),

    hero: {
      headline: hero?.headline ?? "",
      visible: Boolean(hero?.visible),
      primaryButton: {
        label: hero?.primaryButton?.label ?? "",
        visible: Boolean(hero?.primaryButton?.visible),
      },
    },

    joinUs: {
      visible: Boolean(joinUsSection?.visible ?? joinUsCard?.visible),
      label: joinUsCard?.label ?? "",
      title: joinUsCard?.title ?? "",
      description: joinUsCard?.description ?? "",
      imageSrc: joinUsCard?.imageSrc ?? "",
      items: (joinUsCard?.list ?? []).map((entry) => ({
        id: Number(entry.id ?? 0),
        step: entry.step ?? "",
        title: entry.title ?? "",
        description: entry.description ?? "",
        buttonTitle: entry.button?.title ?? "",
      })),
    },

    contact: {
      visible: Boolean(contactSection?.visible ?? contactCard?.visible),
      label: contactCard?.label ?? "",
      title: contactCard?.title ?? "",
      description: contactCard?.description ?? "",
      imageSrc: contactCard?.imageSrc ?? "",
      items: (contactCard?.list ?? []).map((entry) => ({
        id: String(entry.id ?? ""),
        title: entry.title ?? "",
        pathUrl: entry.pathUrl ?? "",
        paragraph: entry.paragraph ?? "",
        buttonText: entry.buttonText ?? "",
      })),
    },

    ourValue: {
      visible: Boolean(ourValueSection?.visible ?? ourValueCard?.visible),
      label: ourValueCard?.label ?? "",
      title: ourValueCard?.title ?? "",
      description: ourValueCard?.description ?? "",
      items: (ourValueCard?.list ?? []).map((entry) => ({
        id: Number(entry.id ?? 0),
        title: entry.title ?? "",
        iconAlt: entry.iconAlt ?? "",
        iconSrc: entry.iconSrc ?? "",
        description: entry.description ?? "",
      })),
    },

    ourMission: {
      visible: Boolean(ourMissionSection?.visible ?? ourMissionHeader?.visible),
      label: ourMissionHeader?.label ?? "",
      title: ourMissionHeader?.title ?? "",
      description: ourMissionHeader?.description ?? "",
      image: ourMissionHeader?.image ?? "",
    },

    differentiator: {
      visible: Boolean(differentiatorSection?.visible ?? differentiatorCard?.visible),
      label: differentiatorCard?.label ?? "",
      title: differentiatorCard?.title ?? "",
      description: differentiatorCard?.description ?? "",
      items: (differentiatorCard?.list ?? []).map((entry) => ({
        id: Number(entry.id ?? 0),
        title: entry.title ?? "",
        iconAlt: entry.iconAlt ?? "",
        iconSrc: entry.iconSrc ?? "",
        description: entry.description ?? "",
      })),
    },

    meta: {
      key: company.key ?? "",
    },
  };
}